// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchCompanyShortDescription, signOffCompanyShortDescription } from '../services/shortDescriptionService.ts'
import type { CompanyShortDescription } from '../../../state_manager/data_models/company_short_description.ts'

/**
 * useCompanyShortDescription — a company's short description for reports,
 * with who wrote it and its sign-off. A per-record sub-resource of the
 * company form (the account read does not carry it), read when the Details
 * tab mounts and again after an edit or a sign-off.
 */
export function useCompanyShortDescription(accountId: string | null | undefined) {
  const [record, setRecord] = useState<CompanyShortDescription | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [signingOff, setSigningOff] = useState(false)

  const refetch = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    const res = await fetchCompanyShortDescription(accountId, getAccessToken(store.getState().auth))
    setLoading(false)
    if (res.error || !res.data) {
      setError(res.error || 'The short description could not be loaded.')
      return
    }
    setError(null)
    setRecord(res.data)
  }, [accountId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  /** Sign off the agent's text; the error to show, or null. */
  const signOff = useCallback(async (): Promise<string | null> => {
    if (!accountId) return null
    setSigningOff(true)
    const res = await signOffCompanyShortDescription(accountId, getAccessToken(store.getState().auth))
    setSigningOff(false)
    if (res.error || !res.data) return res.error || 'The short description could not be signed off.'
    setRecord(res.data)
    return null
  }, [accountId])

  return { record, loading, error, refetch, signOff, signingOff }
}
